// src/utils/useAlerts.js
/**
 * useAlerts
 *
 * - Subscribes to the SSE endpoint using your production URL constants.
 * - Parses incoming alerts exactly the same way you did in App.js,
 *   builds the same formattedAlert object and pushes it to currentAlerts.
 * - Persists shownViewerAlerts in localStorage (to avoid duplicates for VIEWER role).
 * - Calls playNotificationSound() once per batch and shows a browser notification
 *   for each formattedAlert (using the formattedAlert fields).
 * - Requests notification permission on mount (so browser notifications work in background).
 *
 * Usage:
 *   const { currentAlerts, showSseModal, dismissAlerts } = useAlerts(token, role);
 */

import { useEffect, useState } from "react";
import axios from "axios";
import * as con from "../../GlobalConstants";
import {
  playNotificationSound,
  showBrowserNotification,
  requestNotificationPermission,
} from "../../utils/notifications"; // src/utils/notifications.js
import { RefRole, RefToken } from "./AuthenticationHook";

// mapAlertType as you defined in your app (kept here to be self-contained)
function mapAlertType(type) {
  switch (type) {
    case "above":
      return "Alza";
    case "below":
      return "Baja";
    default:
      return type;
  }
}

// localStorage helpers
function loadShownViewerAlerts() {
  try {
    const stored = localStorage.getItem("shownViewerAlerts");
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error("Error loading shownViewerAlerts from localStorage", error);
    return [];
  }
}

function saveShownViewerAlerts(list) {
  try {
    localStorage.setItem("shownViewerAlerts", JSON.stringify(list));
  } catch (error) {
    console.error("Error saving shownViewerAlerts to localStorage", error);
  }
}

export function useFetchCOPAlerts() {

  const role = RefRole()
  const token = RefToken()
  const [currentAlerts, setCurrentAlerts] = useState([]);
  const [showSseModal, setShowSseModal] = useState(false);
  const [shownViewerAlerts, setShownViewerAlerts] = useState(loadShownViewerAlerts());

  // Request notification permission once (so browser notifications work even if tab is in background)
  useEffect(() => {
    requestNotificationPermission().catch((err) => {
      // requestNotificationPermission resolves to a string, but handle unexpected errors just in case
      console.warn("requestNotificationPermission error:", err);
    });
  }, []);

  // Update tab title when alerts/modal change (same behavior as your App.js)
  useEffect(() => {
    if (currentAlerts.length > 0 && showSseModal) {
      document.title = `(${currentAlerts.length}) Lumen`;
    } else {
      document.title = "Lumen";
    }
  }, [currentAlerts, showSseModal]);

  // SSE subscription effect (uses production URL from your constants)
  useEffect(() => {
    if (!token || !role) return;
    const sseUrl = `${con.rest_base_url}${con.sse_api}${token}`;
    const source = new EventSource(sseUrl);

    source.onmessage = async (event) => {
      try {
        const incoming = JSON.parse(event.data);

        // In your App.js you iterate directly incoming as an array: keep that behavior
        const alertsArray = Array.isArray(incoming) ? incoming : incoming.alerts || [];
        const newAlerts = [];

        for (const alert of alertsArray) {
          // If viewer, skip already-shown alerts
          if (role === con.VIEWER && shownViewerAlerts.includes(alert.id)) {
            continue;
          }

          // Format reached date exactly like in your App.js
          const reachedDate = new Date(alert.reached).toLocaleString("es-CO", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          });

          // THIS is your formattedAlert — we use it verbatim
          const formattedAlert = {
            id: alert.id,
            type: mapAlertType(alert.alert_type),
            level: alert.level,
            reachedDate,
          };

          newAlerts.push(formattedAlert);

          // For non-viewer roles, mark immediately as notified (same endpoint you used)
          if (role !== con.VIEWER) {
            try {
              await axios.patch(
                `${con.rest_base_url}${con.transactions_api}${con.usdcop_alerts_mark_notified}`,
                { alert_ids: [alert.id] },
                { headers: {"Authorization" : `Token ${token}`} },
              );
            } catch (err) {
              console.error("Error marking SSE alert as notified:", err);
            }
          } else {
            // Viewer: persist as shown so we don't show it twice
            setShownViewerAlerts((prevList) => {
              const newList = [...prevList, alert.id];
              saveShownViewerAlerts(newList);
              return newList;
            });
          }
        }

        if (newAlerts.length === 0) {
          return;
        }

        // update state & open modal (same behavior as original App.js)
        setCurrentAlerts((prev) => [...prev, ...newAlerts]);
        setShowSseModal(true);

        // Notifications:
        // - try to play the sound once per batch
        // - show a browser notification for each formattedAlert (we use the same formattedAlert fields as you requested)
        try {
          // play sound (may be blocked by browser if there's no user gesture)
          await playNotificationSound().catch((err) => {
            // don't throw — log and continue to show browser notifications as fallback
            console.warn("playNotificationSound failed (likely autoplay blocked):", err);
          });

          // Show browser notification per alert (bodies built with formattedAlert fields)
          for (const fa of newAlerts) {
            showBrowserNotification("Nueva alerta en Lumen", {
              body: `${fa.type} | Nivel: ${fa.level} | ${fa.reachedDate}`,
              icon: "/icons/alert.png", // optional; change to your icon in public/
            });
          }
        } catch (notifyErr) {
          console.error("Error handling notifications:", notifyErr);
        }
      } catch (errParse) {
        console.error("Error parsing SSE data:", errParse);
      }
    };

    source.onerror = (err) => {
      console.error("SSE Error:", err);
    };

    return () => {
      source.close();
    };
  }, [token, role, shownViewerAlerts]);

  // Dismiss function (used by your Modal accept button)
  function dismissAlerts() {
    setShowSseModal(false);
    setCurrentAlerts([]);

  }

  return {
    currentAlerts,
    showSseModal,
    setShowSseModal,
    dismissAlerts,
  };
}
